import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useRTL } from '../../context/RTLContext';

const SimpleOnboardingModal = ({ isOpen, onClose }) => {
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL, direction } = useRTL();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    marketLocality: '', // New field for market selection
    businessName: '',
    businessWebsite: '',
    industry: '',
    industryOther: '', // For custom industry input
    targetAudience: '',
    buyerRole: '',
    geographicFocus: '',
    geographicOther: '', // For custom geographic input
    solutionCategory: '',
    keyDifferentiators: '',
    competitors: '',
    businessStage: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  // Surfaces a "taking longer than expected" prompt after 120s of generation
  const [generationSlow, setGenerationSlow] = useState(false);
  // Visible countdown on the success screen (3 → 0) before auto-redirect
  const [successCountdown, setSuccessCountdown] = useState(3);

  // Slow-generation timer: arms when generation begins, disarms on success/error
  React.useEffect(() => {
    if (!isGenerating) {
      setGenerationSlow(false);
      return undefined;
    }
    const tm = setTimeout(() => setGenerationSlow(true), 120 * 1000);
    return () => clearTimeout(tm);
  }, [isGenerating]);

  // Success countdown + auto-redirect
  React.useEffect(() => {
    if (!isSuccess) return undefined;
    setSuccessCountdown(3);
    const tick = setInterval(() => {
      setSuccessCountdown((n) => Math.max(0, n - 1));
    }, 1000);
    const redirect = setTimeout(() => {
      onClose();
      navigate('/dashboard');
    }, 3000);
    return () => { clearInterval(tick); clearTimeout(redirect); };
  }, [isSuccess, navigate, onClose]);

  // Disable escape key - onboarding cannot be skipped
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // Do nothing - onboarding is mandatory
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const validateUrl = (url) => {
    if (!url || !url.trim()) return false; // Required field

    try {
      // Add protocol if missing
      let validUrl = url.trim();
      if (!validUrl.match(/^https?:\/\//)) {
        validUrl = `https://${validUrl}`;
      }

      new URL(validUrl); // Validate URL format
      return validUrl;
    } catch {
      return false;
    }
  };

  const handleNextStep = async () => {
    setError('');

    // Validate step 0 - Market selection
    if (currentStep === 0) {
      if (!formData.marketLocality) {
        setError(t('onboarding.validation.marketLocalityRequired') || 'Please select your primary market');
        return;
      }
      // Set language based on market selection
      const targetLang = formData.marketLocality === 'israel' ? 'he' : 'en';
      if (i18n.language !== targetLang) {
        await i18n.changeLanguage(targetLang);
        document.documentElement.dir = targetLang === 'he' ? 'rtl' : 'ltr';
        document.documentElement.lang = targetLang;
        localStorage.setItem('eyehill_language', targetLang);
      }
      // Auto-set geographic focus for Israeli market
      if (formData.marketLocality === 'israel' && !formData.geographicFocus) {
        setFormData(prev => ({ ...prev, geographicFocus: 'Israel' }));
      }
    }

    // Validate step 1 - Business info
    if (currentStep === 1) {
      if (!formData.businessName.trim()) {
        setError(t('onboarding.validation.businessNameRequired') || 'Business name is required');
        return;
      }
      if (formData.businessName.trim().length < 2) {
        setError(t('onboarding.validation.businessNameMinLength') || 'Business name must be at least 2 characters');
        return;
      }
      if (!formData.businessWebsite.trim()) {
        setError(t('onboarding.validation.businessWebsiteRequired') || 'Business website is required');
        return;
      }
      const validatedUrl = validateUrl(formData.businessWebsite);
      if (!validatedUrl) {
        setError(t('onboarding.validation.invalidWebsiteURL') || 'Please enter a valid website URL');
        return;
      }
      setFormData({ ...formData, businessWebsite: validatedUrl });
    }

    // Validate step 2
    if (currentStep === 2) {
      if (!formData.industry) {
        setError(t('onboarding.validation.industryRequired') || 'Please select your industry');
        return;
      }
      if (formData.industry === 'Other' && !formData.industryOther.trim()) {
        setError(t('onboarding.validation.industryOtherRequired') || 'Please specify your industry');
        return;
      }
      if (!formData.targetAudience) {
        setError(t('onboarding.validation.targetAudienceRequired') || 'Please select your target audience');
        return;
      }
      if (!formData.buyerRole) {
        setError(t('onboarding.validation.buyerRoleRequired') || 'Please select the buyer role');
        return;
      }
      if (!formData.geographicFocus) {
        setError(t('onboarding.validation.geographicFocusRequired') || 'Please select your geographic focus');
        return;
      }
      if ((formData.geographicFocus === 'Specific Country' || formData.geographicFocus === 'Specific State/Region') && !formData.geographicOther.trim()) {
        setError(t('onboarding.validation.geographicOtherRequired') || 'Please specify your geographic location');
        return;
      }
    }

    // Validate step 3
    if (currentStep === 3) {
      if (!formData.solutionCategory.trim()) {
        setError(t('onboarding.validation.solutionCategoryRequired') || 'Solution category is required');
        return;
      }
      if (!formData.competitors.trim()) {
        setError(t('onboarding.validation.competitorsRequired') || 'Please enter at least one competitor');
        return;
      }
      if (!formData.businessStage) {
        setError(t('onboarding.validation.businessStageRequired') || 'Please select your business stage');
        return;
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setError('');
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);
    setIsGenerating(true);

    try {
      // First, submit the basic onboarding data
      const startResponse = await fetch(`${process.env.REACT_APP_API_URL}/onboarding/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessName: formData.businessName.trim(),
          businessWebsite: formData.businessWebsite,
          marketLocality: formData.marketLocality
        })
      });

      if (!startResponse.ok) {
        const result = await startResponse.json();
        throw new Error(result.error || 'Failed to save business information');
      }

      // Prepare the context data, using custom values if "Other" or "Specific" was selected
      const contextData = {
        industry: formData.industry === 'Other' ? formData.industryOther.trim() : formData.industry,
        targetAudience: formData.targetAudience,
        buyerRole: formData.buyerRole,
        geographicFocus: (formData.geographicFocus === 'Specific Country' || formData.geographicFocus === 'Specific State/Region')
          ? formData.geographicOther.trim()
          : formData.geographicFocus,
        solutionCategory: formData.solutionCategory.trim(),
        keyDifferentiators: formData.keyDifferentiators.trim() || null,
        competitors: formData.competitors.trim(),
        businessStage: formData.businessStage // This will now have the correct snake_case value
      };

      // Then submit the context data which will trigger query generation
      const contextResponse = await fetch(`${process.env.REACT_APP_API_URL}/onboarding/context`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contextData)
      });

      const contextResult = await contextResponse.json();

      if (!contextResponse.ok) {
        throw new Error(contextResult.error || 'Failed to generate queries');
      }

      // Success — auto-redirect is owned by the countdown effect above
      setIsSuccess(true);
      setIsGenerating(false);
      setIsLoading(false);
      updateUser({ onboardingCompleted: true });
    } catch (error) {
      setError(error.message || t('common.errors.unexpected', 'Something went wrong. Please try again.'));
      setIsLoading(false);
      setIsGenerating(false);
    }
  };

  const handleRetryGeneration = () => {
    setError('');
    handleSubmit();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-md flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        // Prevent closing by clicking backdrop - onboarding is mandatory
        e.stopPropagation();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="relative max-w-lg w-full mx-auto"
      >
        <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-3xl p-6 sm:p-8 relative overflow-hidden max-h-[90vh] overflow-y-auto">
          {/* Decorative gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent dark:from-[#262626] dark:to-transparent pointer-events-none" />

          {/* Close button removed - onboarding is mandatory */}

          {/* Progress indicator */}
          {!isSuccess && !isGenerating && currentStep > 0 && (
            <div className="flex items-center justify-center gap-2 mb-6 relative">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    step === currentStep
                      ? 'w-8 bg-blue-600'
                      : step < currentStep
                        ? 'w-2 bg-blue-400'
                        : 'w-2 bg-gray-300 dark:bg-[#3a3a3a]'
                  }`}
                />
              ))}
            </div>
          )}

          <AnimatePresence mode="wait">
            {isGenerating ? (
              <motion.div
                key="generating"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8 relative"
              >
                <div className="w-12 h-12 mx-auto mb-6 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>

                <h3 className="text-xl font-bold text-gray-900 dark:text-[#f5f5f5] mb-2">
                  {t('onboarding.generating.title', 'Generating your personalized queries')}
                </h3>

                <p className="text-sm text-gray-600 dark:text-[#c4c4c4] mb-4">
                  {t('onboarding.generating.subtitle', 'AI is analyzing your business context to create 25 targeted search queries…')}
                </p>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-4">
                  <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                    {t('onboarding.generating.contextHint', 'This may take 30–60 seconds as we generate queries optimized for your:')}
                  </p>
                  <ul className="text-xs mt-2 space-y-1 text-blue-600 dark:text-blue-300">
                    <li>• {formData.industry === 'Other' ? formData.industryOther : formData.industry} {t('onboarding.generating.industry', 'industry')}</li>
                    <li>• {formData.targetAudience} {t('onboarding.generating.market', 'market')}</li>
                    <li>• {(formData.geographicFocus === 'Specific Country' || formData.geographicFocus === 'Specific State/Region') ? formData.geographicOther : formData.geographicFocus} {t('onboarding.generating.region', 'region')}</li>
                  </ul>
                </div>

                {generationSlow && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/40 rounded-xl p-4 text-left">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                      {t('onboarding.generating.slowTitle', 'This is taking longer than expected.')}
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-200/80 mb-3">
                      {t('onboarding.generating.slowBody', 'The AI service may be busy. You can keep waiting, or try again.')}
                    </p>
                    <button
                      type="button"
                      onClick={handleRetryGeneration}
                      className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 transition-colors"
                    >
                      {t('onboarding.generating.retry', 'Try again')}
                    </button>
                  </div>
                )}
              </motion.div>
            ) : isSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8 relative"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                  className="w-20 h-20 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center"
                >
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>

                <h3 className="text-xl font-bold text-gray-900 dark:text-[#f5f5f5] mb-2">
                  {t('onboarding.success.title', 'Welcome aboard!')}
                </h3>

                <p className="text-gray-600 dark:text-[#c4c4c4] mb-4">
                  {t('onboarding.success.subtitle', 'Your 25 personalized queries are ready.')}
                </p>

                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-4">
                  <p className="text-xs text-green-700 dark:text-green-300 font-medium">
                    {t('onboarding.success.detail', '✅ Created 5 query groups based on your business context')}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => { onClose(); navigate('/dashboard'); }}
                  className="px-5 py-2.5 bg-[#d8f9b8] text-[#1a1a1a] font-medium rounded-lg hover:bg-[#c8e9a8] transition-colors"
                >
                  {t('onboarding.success.continue', 'Go to dashboard')}
                  {successCountdown > 0 && (
                    <span className="ml-2 opacity-70 text-sm">({successCountdown})</span>
                  )}
                </button>
              </motion.div>
            ) : (
              <>
                {/* Step 0: Market Selection */}
                {currentStep === 0 && (
                  <motion.div
                    key="step0"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="relative"
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-[#f5f5f5] mb-2 text-center">
                      {t('onboarding.marketSelection.title') || 'Choose Your Primary Market'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-[#c4c4c4] mb-6 text-center">
                      {t('onboarding.marketSelection.subtitle') || 'This will set your app language and cannot be changed later'}
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {/* Israel Option */}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, marketLocality: 'israel' });
                          setError('');
                        }}
                        className={`p-6 border-2 rounded-xl transition-all cursor-pointer ${
                          formData.marketLocality === 'israel'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-[#2e2e2e] hover:border-gray-300 dark:hover:border-[#3a3a3a] bg-white dark:bg-[#262626]'
                        }`}
                      >
                        <div className="text-4xl mb-3 text-center">🇮🇱</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-[#f5f5f5]">
                          {t('onboarding.marketSelection.israel') || 'Israel'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-[#999999] mt-1">
                          {t('onboarding.marketSelection.israelDesc') || 'Hebrew interface'}
                        </div>
                      </button>

                      {/* Worldwide Option */}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, marketLocality: 'worldwide' });
                          setError('');
                        }}
                        className={`p-6 border-2 rounded-xl transition-all cursor-pointer ${
                          formData.marketLocality === 'worldwide'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-[#2e2e2e] hover:border-gray-300 dark:hover:border-[#3a3a3a] bg-white dark:bg-[#262626]'
                        }`}
                      >
                        <div className="text-4xl mb-3 text-center">🌍</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-[#f5f5f5]">
                          {t('onboarding.marketSelection.worldwide') || 'Rest of World'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-[#999999] mt-1">
                          {t('onboarding.marketSelection.worldwideDesc') || 'English interface'}
                        </div>
                      </button>
                    </div>

                    {/* Error message */}
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-600 text-sm text-center font-medium mb-4"
                      >
                        {error}
                      </motion.div>
                    )}

                    {/* Next button */}
                    <button
                      onClick={handleNextStep}
                      disabled={!formData.marketLocality}
                      className="w-full bg-[#d8f9b8] hover:bg-[#c8e9a8] text-gray-900 px-6 py-3 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {t('common.buttons.continue') || 'Continue'}
                    </button>
                  </motion.div>
                )}

                {/* Step 1: Business Information */}
                {currentStep === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="relative"
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-[#f5f5f5] mb-4 text-center">
                      {t('onboarding.title')}{t('onboarding.subtitle') && ` ${t('onboarding.subtitle')}`}
                    </h2>

                    <div className="space-y-4">
                      {/* Business Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.businessInfo.businessName')} *
                        </label>
                        <input
                          type="text"
                          value={formData.businessName}
                          onChange={(e) => {
                            setFormData({ ...formData, businessName: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-xl text-gray-900 dark:text-[#f5f5f5] placeholder-gray-400 dark:placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#2e2e2e] transition-all"
                          placeholder={t('onboarding.businessInfo.businessNamePlaceholder')}
                          disabled={isLoading}
                          autoFocus
                        />
                      </div>

                      {/* Business Website */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.businessInfo.businessWebsite')} *
                        </label>
                        <input
                          type="text"
                          value={formData.businessWebsite}
                          onChange={(e) => {
                            setFormData({ ...formData, businessWebsite: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-xl text-gray-900 dark:text-[#f5f5f5] placeholder-gray-400 dark:placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#2e2e2e] transition-all"
                          placeholder={t('onboarding.businessInfo.businessWebsitePlaceholder')}
                          disabled={isLoading}
                        />
                      </div>

                      {/* Error message */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-600 text-sm text-center font-medium"
                        >
                          {error}
                        </motion.div>
                      )}

                      {/* Next button */}
                      <button
                        onClick={handleNextStep}
                        disabled={isLoading}
                        className="w-full bg-[#d8f9b8] hover:bg-[#c8e9a8] text-gray-900 px-6 py-3 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isRTL ? `${t('common.buttons.next')} ←` : `${t('common.buttons.next')} →`}
                      </button>

                      {/* Skip link removed - onboarding is mandatory */}
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Market Context */}
                {currentStep === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="relative"
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-[#f5f5f5] mb-6 text-center">
                      {t('onboarding.steps.marketTitle')}
                    </h2>

                    <div className="space-y-4">
                      {/* Industry */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.industryDetails.industry')} *
                        </label>
                        <select
                          value={formData.industry}
                          onChange={(e) => {
                            setFormData({ ...formData, industry: e.target.value, industryOther: '' });
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-xl text-gray-900 dark:text-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#2e2e2e] transition-all"
                        >
                          <option value="">{t('onboarding.industryDetails.industryPlaceholder')}</option>
                          <option value="SaaS">{t('onboarding.industryDetails.industryOptions.saas')}</option>
                          <option value="E-commerce">{t('onboarding.industryDetails.industryOptions.ecommerce')}</option>
                          <option value="Healthcare">{t('onboarding.industryDetails.industryOptions.healthcare')}</option>
                          <option value="Finance">{t('onboarding.industryDetails.industryOptions.finance')}</option>
                          <option value="Cybersecurity">{t('onboarding.industryDetails.industryOptions.cybersecurity')}</option>
                          <option value="Marketing/Advertising">{t('onboarding.industryDetails.industryOptions.marketingAdvertising')}</option>
                          <option value="Education">{t('onboarding.industryDetails.industryOptions.education')}</option>
                          <option value="Manufacturing">{t('onboarding.industryDetails.industryOptions.manufacturing')}</option>
                          <option value="Other">{t('onboarding.industryDetails.industryOptions.other')}</option>
                        </select>

                        {/* Show text input if Other is selected */}
                        {formData.industry === 'Other' && (
                          <input
                            type="text"
                            value={formData.industryOther}
                            onChange={(e) => {
                              setFormData({ ...formData, industryOther: e.target.value });
                              setError('');
                            }}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-[#2e2e2e] rounded-xl text-gray-900 dark:text-[#f5f5f5] bg-white dark:bg-[#262626] placeholder-gray-400 dark:placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mt-2"
                            placeholder={t('onboarding.industryDetails.industryOtherPlaceholder')}
                          />
                        )}
                      </div>

                      {/* Target Audience */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.industryDetails.targetAudience')} *
                        </label>
                        <select
                          value={formData.targetAudience}
                          onChange={(e) => {
                            setFormData({ ...formData, targetAudience: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-xl text-gray-900 dark:text-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#2e2e2e] transition-all"
                        >
                          <option value="">{t('onboarding.industryDetails.targetAudiencePlaceholder')}</option>
                          <option value="Enterprise (Fortune 500)">{t('onboarding.industryDetails.targetAudienceOptions.enterprise')}</option>
                          <option value="Mid-market">{t('onboarding.industryDetails.targetAudienceOptions.midMarket')}</option>
                          <option value="SMB (Small/Medium Business)">{t('onboarding.industryDetails.targetAudienceOptions.smb')}</option>
                          <option value="Startups">{t('onboarding.industryDetails.targetAudienceOptions.startups')}</option>
                          <option value="Consumers">{t('onboarding.industryDetails.targetAudienceOptions.consumers')}</option>
                          <option value="Government">{t('onboarding.industryDetails.targetAudienceOptions.government')}</option>
                          <option value="Non-profit">{t('onboarding.industryDetails.targetAudienceOptions.nonProfit')}</option>
                        </select>
                      </div>

                      {/* Buyer Role */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.industryDetails.buyerRole')} *
                        </label>
                        <select
                          value={formData.buyerRole}
                          onChange={(e) => {
                            setFormData({ ...formData, buyerRole: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-xl text-gray-900 dark:text-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#2e2e2e] transition-all"
                        >
                          <option value="">{t('onboarding.industryDetails.buyerRolePlaceholder')}</option>
                          <option value="C-Suite/Executive">{t('onboarding.industryDetails.buyerRoleOptions.cSuite')}</option>
                          <option value="IT Decision Makers">{t('onboarding.industryDetails.buyerRoleOptions.itDecisionMakers')}</option>
                          <option value="Marketing Leaders">{t('onboarding.industryDetails.buyerRoleOptions.marketingLeaders')}</option>
                          <option value="Product Managers">{t('onboarding.industryDetails.buyerRoleOptions.productManagers')}</option>
                          <option value="Developers/Engineers">{t('onboarding.industryDetails.buyerRoleOptions.developersEngineers')}</option>
                          <option value="End Users">{t('onboarding.industryDetails.buyerRoleOptions.endUsers')}</option>
                          <option value="Procurement Teams">{t('onboarding.industryDetails.buyerRoleOptions.procurementTeams')}</option>
                        </select>
                      </div>

                      {/* Geographic Focus */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.industryDetails.geographicFocus')} *
                        </label>
                        <select
                          value={formData.geographicFocus}
                          onChange={(e) => {
                            setFormData({ ...formData, geographicFocus: e.target.value, geographicOther: '' });
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-xl text-gray-900 dark:text-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#2e2e2e] transition-all"
                        >
                          <option value="">{t('onboarding.industryDetails.geographicFocusPlaceholder')}</option>
                          <option value="Global">{t('onboarding.industryDetails.geographicOptions.global')}</option>
                          <option value="North America">{t('onboarding.industryDetails.geographicOptions.northAmerica')}</option>
                          <option value="Europe">{t('onboarding.industryDetails.geographicOptions.europe')}</option>
                          <option value="Asia Pacific">{t('onboarding.industryDetails.geographicOptions.asiaPacific')}</option>
                          <option value="Latin America">{t('onboarding.industryDetails.geographicOptions.latinAmerica')}</option>
                          <option value="Middle East & Africa">{t('onboarding.industryDetails.geographicOptions.middleEastAfrica')}</option>
                          <option value="Israel">{t('onboarding.industryDetails.geographicOptions.israel')}</option>
                          <option value="Specific Country">{t('onboarding.industryDetails.geographicOptions.specificCountry')}</option>
                          <option value="Specific State/Region">{t('onboarding.industryDetails.geographicOptions.specificStateRegion')}</option>
                        </select>

                        {/* Show text input if Specific Country or State/Region is selected */}
                        {(formData.geographicFocus === 'Specific Country' || formData.geographicFocus === 'Specific State/Region') && (
                          <input
                            type="text"
                            value={formData.geographicOther}
                            onChange={(e) => {
                              setFormData({ ...formData, geographicOther: e.target.value });
                              setError('');
                            }}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-[#2e2e2e] rounded-xl text-gray-900 dark:text-[#f5f5f5] bg-white dark:bg-[#262626] placeholder-gray-400 dark:placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mt-2"
                            placeholder={formData.geographicFocus === 'Specific Country' ? 'e.g., United States, Germany, Japan' : 'e.g., California, Texas, Ontario'}
                          />
                        )}
                      </div>

                      {/* Error message */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-600 text-sm text-center font-medium"
                        >
                          {error}
                        </motion.div>
                      )}

                      {/* Navigation buttons */}
                      <div className="flex gap-3">
                        <button
                          onClick={handlePreviousStep}
                          className="flex-1 bg-white dark:bg-[#262626] text-gray-700 dark:text-[#f5f5f5] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#2e2e2e] px-6 py-3 rounded-xl font-medium transition-colors"
                        >
                          {isRTL ? `→ ${t('common.buttons.back')}` : `← ${t('common.buttons.back')}`}
                        </button>
                        <button
                          onClick={handleNextStep}
                          className="flex-1 bg-[#d8f9b8] hover:bg-[#c8e9a8] text-gray-900 px-6 py-3 rounded-xl font-medium transition-colors"
                        >
                          {isRTL ? `${t('common.buttons.next')} ←` : `${t('common.buttons.next')} →`}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Competitive Landscape */}
                {currentStep === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="relative"
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-[#f5f5f5] mb-6 text-center">
                      {t('onboarding.steps.competitiveTitle')}
                    </h2>

                    <div className="space-y-4">
                      {/* Solution Category */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.industryDetails.solutionCategory')} *
                        </label>
                        <input
                          type="text"
                          value={formData.solutionCategory}
                          onChange={(e) => {
                            setFormData({ ...formData, solutionCategory: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-xl text-gray-900 dark:text-[#f5f5f5] placeholder-gray-400 dark:placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#2e2e2e] transition-all"
                          placeholder={t('onboarding.industryDetails.solutionCategoryPlaceholder')}
                        />
                      </div>

                      {/* Key Differentiators */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.competitorInfo.keyDifferentiators')} ({t('common.optional')})
                        </label>
                        <textarea
                          value={formData.keyDifferentiators}
                          onChange={(e) => {
                            setFormData({ ...formData, keyDifferentiators: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-[#2e2e2e] rounded-xl text-gray-900 dark:text-[#f5f5f5] bg-white dark:bg-[#262626] placeholder-gray-400 dark:placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          placeholder={t('onboarding.competitorInfo.keyDifferentiatorsPlaceholder')}
                          rows="3"
                        />
                      </div>

                      {/* Competitors */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.competitorInfo.competitors')} *
                        </label>
                        <input
                          type="text"
                          value={formData.competitors}
                          onChange={(e) => {
                            setFormData({ ...formData, competitors: e.target.value });
                            setError('');
                          }}
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-xl text-gray-900 dark:text-[#f5f5f5] placeholder-gray-400 dark:placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:bg-white dark:focus:bg-[#2e2e2e] transition-all"
                          placeholder={t('onboarding.competitorInfo.competitorsPlaceholder')}
                        />
                        <p className="text-xs text-gray-600 dark:text-[#999999] mt-1">
                          {t('onboarding.competitorInfo.competitorsSeparator')}
                        </p>
                      </div>

                      {/* Business Stage */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-2">
                          {t('onboarding.businessInfo.businessStage')} *
                        </label>
                        <div className="space-y-2">
                          {[
                            { value: 'entering_new_market', label: t('onboarding.businessInfo.businessStageOptions.enteringNewMarket') },
                            { value: 'defending_position', label: t('onboarding.businessInfo.businessStageOptions.defendingPosition') },
                            { value: 'category_creation', label: t('onboarding.businessInfo.businessStageOptions.categoryCreation') },
                            { value: 'growing_market_share', label: t('onboarding.businessInfo.businessStageOptions.growingMarketShare') }
                          ].map((option) => (
                            <label key={option.value} className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name="businessStage"
                                value={option.value}
                                checked={formData.businessStage === option.value}
                                onChange={(e) => {
                                  setFormData({ ...formData, businessStage: e.target.value });
                                  setError('');
                                }}
                                className="mr-3 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-[#c4c4c4]">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Error message */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-600 text-sm text-center font-medium"
                        >
                          {error}
                        </motion.div>
                      )}

                      {/* Navigation buttons */}
                      <div className="flex gap-3">
                        <button
                          onClick={handlePreviousStep}
                          disabled={isLoading}
                          className="flex-1 bg-white dark:bg-[#262626] text-gray-700 dark:text-[#f5f5f5] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#2e2e2e] px-6 py-3 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isRTL ? `→ ${t('common.buttons.back')}` : `← ${t('common.buttons.back')}`}
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={isLoading}
                          className="flex-1 bg-[#d8f9b8] hover:bg-[#c8e9a8] text-gray-900 px-6 py-3 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isLoading ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              {t('onboarding.generating.button') || 'Generating...'}
                            </span>
                          ) : (
                            t('onboarding.submit.button') || 'Generate My Queries'
                          )}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default SimpleOnboardingModal;